import { useState, useEffect, useRef } from "react"
import { useVoiceRecording } from "@/hooks/useVoiceRecording"
import { Square, X, AlertTriangle, Mic } from "lucide-react"
import { Button } from "@/components/ui/button"

interface VoiceRecordingScreenProps {
  onComplete: (data: { key: string | null; uploadId: string | null; parts: Array<{ etag: string; part_number: number }>; elapsed: number }) => void
  onCancel: () => void
}

function formatTime(seconds: number): string {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
}

export function VoiceRecordingScreen({ onComplete, onCancel }: VoiceRecordingScreenProps) {
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const { isRecording, elapsed, savedThrough, deviceName, start, stop, cancel, getAnalyserData } = useVoiceRecording()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animationRef = useRef<number>(0)
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768

  useEffect(() => {
    start()
    return () => {
      cancelAnimationFrame(animationRef.current)
    }
  }, [])

  useEffect(() => {
    if (!isRecording) return

    function drawWaveform() {
      const canvas = canvasRef.current
      if (!canvas) return

      const ctx = canvas.getContext("2d")
      if (!ctx) return

      const data = getAnalyserData()
      const width = canvas.width
      const height = canvas.height
      const barWidth = 3
      const gap = 2
      const bars = Math.floor(width / (barWidth + gap))

      ctx.clearRect(0, 0, width, height)

      for (let i = 0; i < bars; i++) {
        // Center-swell: center bars get low frequencies (high energy),
        // edges get high frequencies (low energy)
        const centerOffset = Math.abs(i - bars / 2) / (bars / 2)
        const dataIndex = Math.floor(centerOffset * (data.length - 1))
        const value = data[dataIndex] / 255
        const barHeight = Math.max(4, value * height * 0.8)

        ctx.fillStyle = "oklch(0.457 0.24 277.023)"
        ctx.fillRect(
          i * (barWidth + gap),
          (height - barHeight) / 2,
          barWidth,
          barHeight
        )
      }

      animationRef.current = requestAnimationFrame(drawWaveform)
    }

    drawWaveform()
    return () => cancelAnimationFrame(animationRef.current)
  }, [isRecording, getAnalyserData])

  function handleStop() {
    const data = stop()
    onComplete(data)
  }

  function handleCancel() {
    if (elapsed > 5) {
      setShowCancelConfirm(true)
    } else {
      cancel()
      onCancel()
    }
  }

  function confirmCancel() {
    cancel()
    onCancel()
  }

  return (
    <div className="flex flex-col items-center justify-center h-full gap-8 p-8" data-testid="voice-recording-screen">
      {isMobile && (
        <div className="absolute top-16 left-4 right-4 flex items-center gap-2 rounded-lg bg-amber-50 dark:bg-amber-950/30 p-3 text-sm text-amber-700 dark:text-amber-400" data-testid="mobile-warning">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          Keep this screen open while recording
        </div>
      )}

      {/* Timer */}
      <div className="text-6xl font-mono font-light tabular-nums" data-testid="recording-timer">
        {formatTime(elapsed)}
      </div>

      {/* Microphone device name */}
      {deviceName && (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="mic-device-name">
          <Mic className="h-3 w-3" />
          {deviceName}
        </p>
      )}

      {/* Waveform */}
      <canvas
        ref={canvasRef}
        width={300}
        height={80}
        className="w-full max-w-xs"
        data-testid="waveform"
      />

      {/* Saved through indicator */}
      {savedThrough > 0 && (
        <p className="text-sm text-muted-foreground" data-testid="saved-through">
          Saved through {formatTime(savedThrough)}
        </p>
      )}

      {/* Controls */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="lg"
          onClick={handleCancel}
          className="gap-2"
          data-testid="cancel-recording"
        >
          <X className="h-5 w-5" />
          Cancel
        </Button>
        <Button
          size="lg"
          onClick={handleStop}
          className="gap-2 bg-destructive text-destructive-foreground hover:bg-destructive/90"
          data-testid="stop-recording"
        >
          <Square className="h-5 w-5" />
          Stop
        </Button>
      </div>

      {/* Cancel confirmation */}
      {showCancelConfirm && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <div className="rounded-lg border bg-card p-6 shadow-lg">
            <p className="mb-4 text-sm">Discard this recording?</p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowCancelConfirm(false)}>
                Keep recording
              </Button>
              <Button variant="destructive" onClick={confirmCancel}>
                Discard
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
